import React, { useMemo, useCallback } from "react";
import { MdArrowForward, MdUndo } from "react-icons/md";
import cn from "classnames";
import * as R from "ramda";
import { SegmentationMappingLabelRule } from "@blisspointmedia/bpm-types/dist/SegmentationMapping";
import { Button, ButtonType, ButtonFrameworkVariant } from "../Components";
import { getSegmentName, getSegmentValue, omitMetaFields } from "./LabelingRules";
import { CustomSegmentsData } from "./SegmentationMapping";

interface PendingSegmentationEditsProps {
  row: SegmentationMappingLabelRule;
  originalRules: SegmentationMappingLabelRule[];
  customSegments: CustomSegmentsData[];
  isNew?: boolean;
  isDeleted?: boolean;
  onUndo?: (row) => void;
}

const PendingSegmentationEdits: React.FC<PendingSegmentationEditsProps> = ({
  row,
  originalRules,
  customSegments = [],
  isNew = false,
  isDeleted = false,
  onUndo = () => {},
}) => {
  const { id, company, name, isAdvanced, setSegmentId, setSegmentValueId, rulePriority } = row;
  const segmentName = getSegmentName(setSegmentId, customSegments);
  const segmentValueName = getSegmentValue(setSegmentValueId, customSegments);

  const originalRule = useMemo(() => {
    if (isNew || isDeleted) {
      return null;
    }
    const or = originalRules.find(rule => rule.id === id);
    if (!or) {
      return null;
    }
    const originalSegmentName = getSegmentName(or.setSegmentId, customSegments);
    const originalSegmentValueName = getSegmentValue(or.setSegmentValueId, customSegments);
    return { ...or, segmentName: originalSegmentName, segmentValueName: originalSegmentValueName };
  }, [customSegments, id, isDeleted, isNew, originalRules]);

  const changeKeys = useMemo(() => {
    if (originalRule) {
      const sanitizedOriginalRule = omitMetaFields(originalRule);
      const sanitizedRule = omitMetaFields(row);

      const keys = R.union(R.keys(sanitizedOriginalRule), R.keys(sanitizedRule));
      const differingKeys = keys.filter(
        key => !R.equals(sanitizedOriginalRule[key], sanitizedRule[key])
      );

      return differingKeys;
    }
    return [];
  }, [originalRule, row]);

  const isOrderChangeOnly = useMemo((): boolean => {
    if (isNew || isDeleted) {
      return false;
    }
    const hasPriorityKey = R.includes("rulePriority", changeKeys);
    return hasPriorityKey && changeKeys.length === 1;
  }, [changeKeys, isDeleted, isNew]);

  const undoRowChange = useCallback(() => {
    if (!isNew) {
      onUndo(row);
    }
  }, [isNew, onUndo, row]);

  const changeTitle = useMemo(() => {
    if (name) {
      return `${company} - ${segmentName} - ${name}`;
    }
    return `${company} - ${segmentName} to ${segmentValueName} - NEW ${rulePriority}`;
  }, [company, name, rulePriority, segmentName, segmentValueName]);

  const renderChangeString = (attr: string, value: any) => {
    if (value === undefined || value === null) {
      return "Not Set";
    }
    switch (attr) {
      case "name":
        if (isNew) {
          return value ? value : "New Rule Name Set on Save";
        }
        return String(value);
      case "setSegmentId":
        return value || "Not Set";
      case "setSegmentValueId":
        return value || "Not Set";
      case "filterTokens":
        if (isAdvanced && Array.isArray(value) && value.length > 0) {
          return value.map(token => token.label).join(" ");
        }
        return value.length > 0
          ? `* is like ${value.map(filter => filter.label).join(" or ")}`
          : "No filters set";
      case "isAdvanced":
        return value ? "Advanced" : "Basic";
      case "isActive":
        return value ? "Yes" : "No";
      default:
        return String(value);
    }
  };

  const readableAttrName = (attr: string) => {
    switch (attr) {
      case "setSegmentId":
        return "Segment";
      case "setSegmentValueId":
        return "Segment Value";
      case "filterTokens":
        return "If Statement";
      case "isAdvanced":
        return "Rule Type";
      case "isActive":
        return "Enabled";
      default:
        return (
          attr.charAt(0).toUpperCase() +
          attr
            .slice(1)
            .replace(/([A-Z])/g, " $1")
            .trim()
        );
    }
  };

  const editsList = () => {
    return (
      <div className="changeList">
        {Object.keys(row).map(attr => {
          const newValue = row[attr];
          const oldValue = originalRule ? originalRule[attr] : null;
          const valueIsChanged = newValue !== oldValue;

          if (
            attr === "id" ||
            attr === "company" ||
            attr === "updatedAt" ||
            attr === "createdAt" ||
            attr === "createdBy" ||
            attr === "lastUpdatedBy" ||
            attr === "ifStatement"
          ) {
            return null;
          }

          if (!valueIsChanged && !oldValue) {
            return null;
          }

          if (R.equals(newValue, oldValue)) {
            return null;
          }

          return (
            <div className={cn("edit", { isNew, isDeleted })} key={attr}>
              {!isNew && !isDeleted && !newValue && <div className="deleteButtonSpacer" />}
              <span className="attributeName">{readableAttrName(attr)}:</span>
              {!isNew && !isDeleted && valueIsChanged && (
                <>
                  <span className="oldValue">{renderChangeString(attr, oldValue)}</span>
                  <MdArrowForward />
                </>
              )}
              <span
                className={cn(valueIsChanged || oldValue ? "newValue" : "oldValue", { isDeleted })}
              >
                {renderChangeString(attr, valueIsChanged ? newValue : oldValue)}
              </span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="changeContainer">
      <div className="changeHeader">
        <div className="changeTitle">
          {!isNew && (
            <Button
              className="undoButton"
              size="sm"
              variant={ButtonFrameworkVariant.ICON_ONLY}
              type={ButtonType.EMPTY}
              icon={<MdUndo />}
              onClick={() => undoRowChange()}
              title="Undo this change"
              // We want the button disabled when the only change is the rule priority
              disabled={isOrderChangeOnly}
            />
          )}
          {changeTitle}
        </div>
      </div>
      {editsList()}
    </div>
  );
};

export default PendingSegmentationEdits;
